import { useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useDispatch } from "react-redux";
import { toggleCareerModal } from "../slices/dataSlice";

const CareerDialogueModal = ({ showCareer, content }) => {
  const [pathwayVisible, setPathwayVisible] = useState(false);
  const [pathwayDetail, setPathwayDetail] = useState({});

  const dispatch = useDispatch();

  const PathWays = () => {
    const handlePathwayClose = () => {
      setPathwayVisible(false);
      dispatch(toggleCareerModal(false));
    };

    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <button
              className="text-text-secondary hover:text-border-extrabold"
              onClick={() => setPathwayVisible(false)}
              title="Back to career details"
            >
              <span className="sr-only">Back to career details</span>
              {/* a11y: label for icon-only button */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                {/* a11y: hide decorative icon */}
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <DialogTitle className="text-2xl font-bold text-text-strong ml-2">
              {pathwayDetail?.label} Pathway
            </DialogTitle>
          </div>
          <button
            className="text-text-secondary hover:text-border-extrabold"
            onClick={handlePathwayClose}
            title="Close modal"
          >
            <span className="sr-only">Close modal</span>
            {/* a11y: label for icon-only button */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              {/* a11y: hide decorative icon */}
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-3">
          <div className="bg-cyan-200 p-4 rounded-lg text-pathway-text text-sm">
            <p>
              <strong className=" mb-1">{pathwayDetail?.label}:</strong>{" "}
              {pathwayDetail?.details?.credential}{" "}
            </p>
          </div>
          <div className="bg-cyan-200 p-4 rounded-lg text-pathway-text text-sm space-y-2">
            <p>
              <strong className=" mb-1">Key Courses/Subjects:</strong>{" "}
              {pathwayDetail?.details?.key_courses}
            </p>
            <p>
              <strong className=" mb-1">Essential Skills:</strong>{" "}
              {pathwayDetail?.details?.essential_skills}
            </p>
            <p>
              <strong className=" mb-1">Important Certifications:</strong>{" "}
              {pathwayDetail?.details?.important_certifications}
            </p>
            <p>
              <strong className=" mb-1">Practical Experience:</strong>{" "}
              {pathwayDetail?.details?.practical_experience}
            </p>
            <p>
              <strong className=" mb-1">Career Progression:</strong>{" "}
              {pathwayDetail?.details?.career_progression}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const CareerContent = ({ data }) => {
    const handleShowPathwayDetails = (p) => {
      setPathwayDetail(p);
      setPathwayVisible(true);
    };

    const handleKeyActivate = (e, p) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleShowPathwayDetails(p);
      }
    }; // a11y: keyboard activation for clickable divs

    return (
      <div>
        <div className="p-6 py-3">
          <div className="flex justify-between items-center mb-4">
            <DialogTitle className="text-2xl font-bold text-text-strong">
              {data.profession_name}
            </DialogTitle>
            <button
              className="text-text-secondary hover:text-border-bold"
              onClick={() => dispatch(toggleCareerModal(false))}
              title="Close modal"
            >
              <span className="sr-only">Close modal</span>
              {/* a11y: label for icon-only button */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                {/* a11y: hide decorative icon */}
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <p className="text-text-primary mb-2 text-sm">{data.rationale}</p>
          <div className="space-y-1">
            <div className="flex items-center">
              <span className="text-text-primary font-semibold text-sm w-40">
                Job Demand:
              </span>
              <span
                className={`font-semibold text-xs rounded-full px-3 py-1 ${
                  {
                    high: "bg-success text-white",
                    medium: "bg-warning text-white",
                    low: "bg-category-highlight text-white",
                  }[data?.job_demand?.toLowerCase()] ||
                  "bg-gray-300 text-text-primary"
                }`}
              >
                {data?.job_demand}
              </span>
            </div>
            <div className="flex items-center">
              <span className="text-text-primary font-semibold text-sm w-40">
                Preferred Qualifications:
              </span>
              <span className="bg-info-subtle text-primary-strong text-xs font-semibold px-3 py-1 rounded-full">
                {data?.preferred_qualifications}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 py-3 bg-surface-base border-t border-b">
          <h2 className=" font-semibold text-sm text-text-strong mb-2">
            Salary Range
          </h2>
          <div className="grid grid-cols-2 gap-1 text-sm">
            {data?.salary_range.map((range) => {
              return (
                <>
                  <div key={`${range.label}-label`}>{range.label}: </div>
                  <div
                    key={`${range.label}-value`}
                    className="text-right font-medium"
                  >
                    {range.value}
                  </div>
                </>
              );
            })}
          </div>
        </div>

        <div className="p-6 py-3">
          <h2 className="font-semibold text-sm text-text-strong mb-2">
            Pathways
          </h2>

          <div className="space-y-3">
            <div
              onClick={() =>
                handleShowPathwayDetails(data?.pathways?.certificate)
              }
              onKeyDown={(e) =>
                handleKeyActivate(e, data?.pathways?.certificate)
              }
              role="button"
              tabIndex={0}
              aria-label={`View ${data?.pathways?.certificate?.label} pathway details`}
              className="flex items-center text-sm justify-between bg-primary text-white p-4 rounded-lg shadow-md cursor-pointer hover:bg-primary-hover transition duration-150"
            >
              {/* a11y: keyboard access and label for clickable card */}
              <div>
                <div className="font-semibold">
                  {data?.pathways?.certificate?.label}
                </div>
                <div className="text-sm opacity-90">
                  Duration: {data?.pathways?.certificate?.duration}
                </div>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium mr-3">
                  Salary Range: {data?.pathways?.certificate?.salary_range}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  {/* a11y: hide decorative icon */}
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>

            <div
              onClick={() =>
                handleShowPathwayDetails(data?.pathways?.associates_degree)
              }
              onKeyDown={(e) =>
                handleKeyActivate(e, data?.pathways?.associates_degree)
              }
              role="button"
              tabIndex={0}
              aria-label={`View ${data?.pathways?.associates_degree?.label} pathway details`}
              className="flex items-center text-sm justify-between bg-primary text-white p-4 rounded-lg shadow-md cursor-pointer hover:bg-primary-hover transition duration-150"
            >
              {/* a11y: keyboard access and label for clickable card */}
              <div>
                <div className="font-semibold">
                  {data?.pathways?.associates_degree?.label}
                </div>
                <div className="text-sm opacity-90">
                  Duration: {data?.pathways?.associates_degree?.duration}
                </div>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium mr-3">
                  Salary Range:{" "}
                  {data?.pathways?.associates_degree?.salary_range}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  {/* a11y: hide decorative icon */}
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>

            <div
              onClick={() =>
                handleShowPathwayDetails(data?.pathways?.bachelors_degree)
              }
              onKeyDown={(e) =>
                handleKeyActivate(e, data?.pathways?.bachelors_degree)
              }
              role="button"
              tabIndex={0}
              aria-label={`View ${data?.pathways?.bachelors_degree?.label} pathway details`}
              className="flex items-center text-sm justify-between bg-primary text-white p-4 rounded-lg shadow-md cursor-pointer hover:bg-primary-hover transition duration-150"
            >
              {/* a11y: keyboard access and label for clickable card */}
              <div>
                <div className="font-semibold">
                  {data?.pathways?.bachelors_degree?.label}
                </div>
                <div className="text-sm opacity-90">
                  Duration: {data?.pathways?.bachelors_degree?.duration}
                </div>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium mr-3">
                  Salary Range: {data?.pathways?.bachelors_degree?.salary_range}
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  {/* a11y: hide decorative icon */}
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 py-3 bg-surface-base border-t border-b">
          <h2 className="font-semibold text-sm text-text-strong mb-2">
            NYC Information
          </h2>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <h3 className="text-sm font-semibold  text-border-extrabold mb-1">
                NYC Starting Salary:
              </h3>
              <p className="text-sm font-bold text-primary-hbg-primary-hover">
                {data?.nyc_info_salary_range}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-border-extrabold mb-2">
                NYC Top Employers:
              </h3>
              <div className="flex flex-wrap gap-2">
                {data?.nyc_top_employers.map((n) => {
                  return (
                    <span
                      key={n}
                      className="bg-info-subtle text-primary-strong text-xs font-semibold px-2.5 py-1 rounded-full"
                    >
                      {n}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={showCareer}
      onClose={() => dispatch(toggleCareerModal(false))}
      className="relative z-10"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-surface-inverse/50 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />

      <div className="fixed inset-0 z-100 w-full overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-0 text-center sm:items-center">
          <DialogPanel
            transition
            className="bg-white shadow-xl rounded-lg max-w-lg w-full overflow-hidden relative text-left transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in data-closed:sm:translate-y-0 data-closed:sm:scale-95"
          >
            {pathwayVisible ? (
              <PathWays data={content} />
            ) : (
              <CareerContent data={content} />
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default CareerDialogueModal;
